import React, { useState, useCallback, useRef } from 'react';
import classnames from 'classnames';
import { useClickAway } from 'react-use';

import Button from 'monday-ui-react-core/dist/Button';
import Tooltip from 'monday-ui-react-core/dist/Tooltip';
import Icon from 'monday-ui-react-core/dist/Icon';
import SettingsIcon from 'monday-ui-react-core/dist/icons/Settings';
import DialogContentContainer from 'monday-ui-react-core/dist/DialogContentContainer';

import './settings-component.scss';
import ButtonGroup from '../button-group/button-group-component';
import { useSettings } from '../../contexts/settings-context';
import { settingsAPI } from '../../api';
import { useMondayContext } from '../../contexts/monday-context';
import currencies from '../../constants/currencies';
import taxes from '../../constants/taxes';
import fees from '../../constants/fees';
import DocumentBrandWrapperComponent from '../document-brand/document-brand-wrapper-component';

const CURRENCY_OPTIONS = [
  { label: currencies.usd.sign, value: currencies.usd.key },
  { label: currencies.eur.sign, value: currencies.eur.key },
  { label: currencies.gbp.sign, value: currencies.gbp.key },
];

const TAX_OPTIONS = [{ ...taxes.vat }, { ...taxes.tax }];
const FEE_OPTIONS = [{ ...fees.shipping }, { ...fees.handling }];

export default function SettingsComponent() {
  const [isSettingsModalOpen, setSettingsModal] = useState(false);
  const dialogRef = useRef(null);
  const { sessionToken } = useMondayContext();
  const { settings, setSettings } = useSettings();

  useClickAway(dialogRef, () => {
    setSettingsModal(false);
  });

  const toggleSettingsModal = useCallback(() => {
    setSettingsModal(!isSettingsModalOpen);
  }, [isSettingsModalOpen, setSettingsModal]);

  const onCurrencyChange = useCallback(
    async (currency) => {
      const newSettings = { ...settings, currency };
      await settingsAPI.updateSettings(sessionToken, newSettings);
      setSettings(newSettings);
    },
    [settings, setSettings, sessionToken]
  );

  const onTaxChange = useCallback(
    async (tax) => {
      const newSettings = { ...settings, tax };
      await settingsAPI.updateSettings(sessionToken, newSettings);
      setSettings(newSettings);
    },
    [settings, setSettings, sessionToken]
  );

  const onFeeChange = useCallback(
    async (fee) => {
      const newSettings = { ...settings, fee };
      await settingsAPI.updateSettings(sessionToken, newSettings);
      setSettings(newSettings);
    },
    [settings, setSettings, sessionToken]
  );

  const updateBrandChange = useCallback(
    async (removeBrand) => {
      const newSettings = { ...settings, removeBrand };
      await settingsAPI.updateSettings(sessionToken, newSettings);
      setSettings(newSettings);
    },
    [settings, setSettings, sessionToken]
  );

  return (
    <div ref={dialogRef} className={classnames('settings-component', { open: isSettingsModalOpen })}>
      <Tooltip
        wrapperClassName="header-tooltip"
        content="Settings"
        position="right"
        containerSelector=".header-component"
      >
        <Button kind={Button.kinds.TERTIARY} onClick={toggleSettingsModal}>
          <div className="button-content">
            <Icon iconType={Icon.type.SVG} icon={SettingsIcon} iconSize={18} />
          </div>
        </Button>
      </Tooltip>

      {isSettingsModalOpen && (
        <div className="settings-modal">
          <DialogContentContainer>
            <div className="settings-content">
              <div className="currency">
                <div className="header">Currency unit</div>
                <ButtonGroup
                  options={CURRENCY_OPTIONS}
                  value={settings?.currency?.value}
                  type={settings?.currency?.type}
                  withCustom
                  onOptionChange={onCurrencyChange}
                />
              </div>
              <div className="tax">
                <div className="header">Tax name</div>
                <ButtonGroup
                  options={TAX_OPTIONS}
                  value={settings?.tax?.value || taxes.vat.value}
                  type={settings?.tax?.type}
                  withCustom
                  onOptionChange={onTaxChange}
                />
              </div>
              <div className="fee">
                <div className="header">Fee type</div>
                <ButtonGroup
                  options={FEE_OPTIONS}
                  value={settings?.fee?.value}
                  type={settings?.fee?.type}
                  withCustom
                  onOptionChange={onFeeChange}
                />
              </div>
              <div className="remove-brand">
                <div className="header">Branding</div>
                <DocumentBrandWrapperComponent
                  removeMondayBrand={settings?.removeBrand}
                  updateUserSetting={updateBrandChange}
                />
              </div>
            </div>
          </DialogContentContainer>
        </div>
      )}
    </div>
  );
}
